import React, { useState, useEffect } from 'react';

/**
 * Provider Configuration Modal
 * Allows editing provider settings including custom API URLs
 */
const ProviderConfigModal = ({ provider, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    model: '',
    baseUrl: '',
    apiKeyEnvVar: '',
    enabled: true,
    priority: 1,
    pricing: {
      inputCostPerMillion: 0,
      outputCostPerMillion: 0,
      currency: 'USD'
    },
    rateLimit: {
      requestsPerMinute: 60,
      tokensPerMinute: 100000,
      requestsPerDay: 10000
    }
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (provider) {
      setFormData({
        displayName: provider.displayName || '',
        model: provider.model || '',
        baseUrl: provider.baseUrl || '',
        apiKeyEnvVar: provider.apiKey ? '***configured***' : '',
        enabled: provider.enabled !== false,
        priority: provider.priority || 1,
        pricing: {
          inputCostPerMillion: provider.pricing?.inputCostPerMillion || 0,
          outputCostPerMillion: provider.pricing?.outputCostPerMillion || 0,
          currency: provider.pricing?.currency || 'USD'
        },
        rateLimit: {
          requestsPerMinute: provider.capabilities?.requestsPerMinute || 60,
          tokensPerMinute: provider.capabilities?.tokensPerMinute || 100000,
          requestsPerDay: provider.capabilities?.requestsPerDay || 10000
        }
      });
    }
  }, [provider]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      await onSave({
        provider_name: provider.name,
        display_name: formData.displayName,
        model_name: formData.model,
        base_url: formData.baseUrl || null,
        enabled: formData.enabled,
        priority: formData.priority,
        configuration: {
          baseUrl: formData.baseUrl
        },
        pricing: formData.pricing,
        rate_limits: formData.rateLimit
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!provider) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 font-mono">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            Configure {provider.displayName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-400">{error}</span>
              </div>
            </div>
          )}

          {/* Basic Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Basic Settings</h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                placeholder="Google Gemini"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Model Name
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                placeholder="gemini-2.5-flash"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Custom API Base URL (Optional)
              </label>
              <input
                type="url"
                value={formData.baseUrl}
                onChange={(e) => handleChange('baseUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm placeholder-gray-500"
                placeholder="https://api.example.com/v1"
              />
              <p className="text-xs text-gray-400 mt-1">
                Leave empty to use default provider endpoint
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-300">
                    Enabled
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Pricing</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Input Cost (per 1M tokens)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.inputCostPerMillion}
                    onChange={(e) => handleNestedChange('pricing', 'inputCostPerMillion', parseFloat(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Output Cost (per 1M tokens)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.outputCostPerMillion}
                    onChange={(e) => handleNestedChange('pricing', 'outputCostPerMillion', parseFloat(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rate Limits */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Rate Limits</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Requests/Minute
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.requestsPerMinute}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerMinute', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tokens/Minute
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.tokensPerMinute}
                  onChange={(e) => handleNestedChange('rateLimit', 'tokensPerMinute', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Requests/Day
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.requestsPerDay}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerDay', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* API Key Info */}
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-400 mb-1">API Key Configuration</h4>
                <p className="text-sm text-blue-300">
                  API keys are configured via environment variables for security.
                  Set <code className="bg-gray-900 border border-gray-700 text-blue-300 px-1 py-0.5 rounded">REACT_APP_{provider.name.toUpperCase()}_API_KEY</code> in your .env file.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Configuration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProviderConfigModal;
